import React, { useLayoutEffect, useState } from 'react';
import { Button } from '../UI';
import { getOnboardingStepTitle } from '../../constants/onboardingStepTitles';
import {
  computeTooltipPosition,
  ONBOARDING_TOOLTIP_HEIGHT,
  ONBOARDING_TOOLTIP_WIDTH,
  type TooltipPosition,
} from '../../utils/onboardingTooltipPosition';
import { Z_INDEX_TOUR_TOOLTIP } from '../../constants/zIndex';
import type { OnboardingStepItem } from '../../types/onboarding';

export interface TourTooltipProps {
  step: OnboardingStepItem;
  stepIndex: number;
  totalSteps: number;
  targetRect: DOMRect | null;
  onGoToAction: () => void;
  onSkip?: () => void;
  onClose: () => void;
  isClosing?: boolean;
}

export const TourTooltip: React.FC<TourTooltipProps> = ({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  onGoToAction,
  onSkip,
  onClose,
  isClosing = false,
}) => {
  const [position, setPosition] = useState<TooltipPosition>(() =>
    computeTooltipPosition(targetRect, ONBOARDING_TOOLTIP_HEIGHT)
  );

  useLayoutEffect(() => {
    setPosition(computeTooltipPosition(targetRect, ONBOARDING_TOOLTIP_HEIGHT));
  }, [targetRect]);

  return (
    <div
      role="dialog"
      aria-label="Guia de configuração"
      className="pointer-events-auto fixed w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
      style={{
        top: position.top,
        left: position.left,
        width: ONBOARDING_TOOLTIP_WIDTH,
        zIndex: Z_INDEX_TOUR_TOOLTIP,
      }}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Passo {stepIndex} de {totalSteps}
      </p>
      <h2 className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
        {getOnboardingStepTitle(step.id)}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {step.supportHint}
      </p>
      <div className="mt-5 flex flex-col gap-2">
        <Button type="button" variant="primary" className="w-full" onClick={onGoToAction}>
          Ir para ação
        </Button>
        {onSkip && (
          <Button type="button" variant="secondary" className="w-full" onClick={onSkip}>
            Pular passo
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={onClose}
          loading={isClosing}
          disabled={isClosing}
        >
          Fechar tour
        </Button>
      </div>
    </div>
  );
};
